import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { isPublicPath, normalizePath } from "../../lib/routeConfig";
import loadingSpinner from "@/assets/loading-spinner.gif";
import { SignInPrompt } from "../SignInPrompt";
import { getGuestToken, validateGuestToken } from "../../lib/guestToken";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - SIMPLIFIED
 * 
 * Uses Supabase best-practice auth pattern:
 * - loading=true until first getSession() completes
 * - isAuthenticated set from session presence
 * - No grace periods or workarounds needed
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { loading, isAuthenticated, isGuest } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Preview mode bypass
  if (searchParams.get('preview') === 'true') {
    return <>{children}</>;
  }

  // Public route bypass
  const path = normalizePath(location.pathname);
  if (isPublicPath(path)) {
    return <>{children}</>;
  }

  // Guest token bypass
  const guestToken = getGuestToken();
  if (guestToken && validateGuestToken(guestToken)) {
    return <>{children}</>;
  }

  // Authenticated or guest → render app
  if (isAuthenticated || isGuest) {
    return <>{children}</>;
  }

  // Still loading → show spinner
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src={loadingSpinner} alt="Laddar" className="h-24 w-24" />
          <p className="text-muted-foreground text-sm font-light">Laddar...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → show prompt
  return <SignInPrompt returnTo={window.location.href} />;
}
